/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useState
} from 'react';
import {makeStyles, Tooltip} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {BulkSettingFieldValues, SCIDeviceType, SCITemperatureFields} from '@ion/core/src/types/entities';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import RadioButton from '@ion/dls/src/components/radio/RadioButton';
import Switch from '@ion/dls/src/components/switch/Switch';
import {ReportDropdownChooser} from '@ion/dls/src/editors/ReportDropdownChooser';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {BulkSCIStyleProps} from './BulkSCISettings';
import {
  maxLigthLevelFields,
  sensorLoggingPeriodList,
  sensorPendingFields,
  temperatureLoggingFields
} from './SCISettingsUtils';

export interface SCISensorsTabProps extends BulkSCIStyleProps {
  deviceModel: string;
  sensorsTabFields: any;
  classes: {
    [key: string]: string;
  };
  handleSensorsFieldChange: Function;
  noUpdate: boolean;
  sciPendingSettingData: any;
  getIsActive: Function;
  pendingSensorValueCheck?: Function;
  bulkSCI?: boolean;
}

const textFieldStyleOverrides = (theme: DLSTheme) => ({
  floatingLabel : {
    color : theme.dls.colors.grey3
  },
  root : {
    '&&' : {
      width     : '66%',
      margin    : '10px 0 10px 130px',
      '&:hover' : {
        backgroundColor : 'transparent'
      }
    }
  },
  underline : {
    backgroundColor : 'transparent'
  },
  inputRoot : {
    '& input' : {

    },
    '&:hover' : {
      backgroundColor : 'transparent'
    }
  }
});

const dropdownStyle = {
  width : '100%'
};
const loggingFieldStyle = {
  height : '78px'
};
const scheduleTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'reportTooltip')
  },
  popper : {
    width         : '158px',
    pointerEvents : 'auto'
  }
}));
/**
 * View or Edit Sensor related information of an SCI device
 */
export const SCISensorsTab = ({
  deviceModel,
  sensorsTabFields,
  classes,
  handleSensorsFieldChange,
  noUpdate,
  sciPendingSettingData,
  getIsActive,
  pendingSensorValueCheck,
  bulkSCI = false,
  isBulkAction = false
}: SCISensorsTabProps) => {
  const [openPendingStatus, setOpenPendingStatus] = useState(false);
  const sensorLogOptions = useMemo(() => {
    const entries = translate('admin-devices:view.sciSettings.sensorsOptions', {
      returnObjects : true
    });
    return Object.entries(entries).map(([value, label]) => {
      return {value, label};
    });
  }, []);

  const isNotTemperatureLogging = useCallback((item): boolean => {
    return (item.label !== translate('admin-devices:view.sciSettings.temperatureLogging'));
  }, []);

  const isTemperatureLoggingFields = useCallback((key): boolean => {
    return temperatureLoggingFields.includes(key);
  }, []);

  const isTemperatureTextFields = useCallback((item): boolean => {
    return (item.lightLevelAlert ||
      item.temperatureAlert ||
      item.impactAlert ||
      item.tipAlert ||
      item.extremeTemperatureLogging);
  }, []);

  const isMinLightLevel = useCallback((key): boolean => {
    return ((key === 'minLightLevel' || key === 'minTemperature') ||
      (deviceModel.includes(SCIDeviceType.STAG09XT) &&
      key === 'sensorLoggingPeriod'));
  }, []);

  const isMaxLightLevel = useCallback((key): boolean => {
    return maxLigthLevelFields.includes(key);
  }, []);

  const isSensorLogging = useCallback((item): boolean => {
    return (!isUndefined(item.sensorLoggingPeriod) &&
      (item.temperatureLogging ||
      deviceModel === SCIDeviceType.STAG09XT));
  }, []);

  const isSyncSensorLog = useCallback((item): boolean => {
    return (!isUndefined(item.syncSensorLog) && item.temperatureLogging);
  }, []);

  const isSyncSensorLogTemp = useCallback((item): boolean => {
    return (!isUndefined(item.sensorLoggingPeriod) && item.syncSensorLog) &&
      (item.temperatureLogging || deviceModel === SCIDeviceType.STAG09XT);
  }, []);

  const isSensorEvenMode = useCallback((item): boolean => {
    return (!isUndefined(item.sensorEventMode) &&
        item.temperatureLogging);
  }, []);

  const toggleTooltip = useCallback((open = false) => {
    return function(e) {
      e.stopPropagation();
      setOpenPendingStatus(open);
    };
  }, [openPendingStatus]);

  const getPendingStatus = (index, item) => {
    const pendingFields = sensorPendingFields(sciPendingSettingData);
    if (pendingFields.length > 2) {
      return (
        <>
          <span>{`(${pendingFields[0]}${pendingFields[1]} `}</span>
          <Tooltip arrow
            classes={scheduleTooltip()}
            open={openPendingStatus}
            title={pendingFields.filter((each, index) => index > 1)}
            placement='bottom-end'
            onClose={toggleTooltip()}
            onOpen={toggleTooltip(true)}>
            <span>...  )</span>
          </Tooltip>
        </>
      );
    } else {
      return pendingSensorValueCheck(index, item);
    }
  };

  const fields = useMemo(() => (
    `${BulkSettingFieldValues.on} ${isBulkAction ? BulkSettingFieldValues.bulkDefault : BulkSettingFieldValues.default} ${BulkSettingFieldValues.off}`
  ), []);

  const getRadioField = (field, value, key) => {
    return (
      <>
        <RadioButton label
          checked={value === true}
          value={BulkSettingFieldValues.on}
          onChange={function(e) { handleSensorsFieldChange(e, field.label, key); }}/>
        <RadioButton label
          checked={value === null}
          value={BulkSettingFieldValues.default}
          onChange={function(e) { handleSensorsFieldChange(e, field.label, key); }}/>
        <RadioButton label
          checked={value === false}
          value={BulkSettingFieldValues.off}
          onChange={function(e) { handleSensorsFieldChange(e, field.label, key); }}/>
      </>
    );
  };

  return (
    <div>
      {(sensorsTabFields?.length && bulkSCI) && (<div className={
        isBulkAction ? classes.bulkRadioLabel : classes.radioLabel}>
        {fields}
      </div>)}
      {sensorsTabFields ? sensorsTabFields.map((item, index) => {
        if (isNotTemperatureLogging(item)) {
          return (<div className={classes.sensorFields}>
            {Object.entries(item).map(([key, value]) => {
              return (
                <div>
                  {(isTemperatureLoggingFields(key)) && (
                    <div className={classes.flexStyle}>
                      <div className={!isBulkAction && classes.sensorFiels}>
                        <FormItem fullWidth>
                          <div className={isBulkAction ? classes.bulkActionsTile : classes.actionsTile}>
                            <BodyTypography className={classes.overridesReportTitle}>
                              {item.label}
                            </BodyTypography>
                            {!bulkSCI
                              ? <Switch disabled={noUpdate}
                                name={item.label}
                                onChange={function(e) {
                                  handleSensorsFieldChange(!value, item.label, key);
                                }}
                                checked={getIsActive(value)}/>
                              : getRadioField(item, value, key)}
                          </div>
                        </FormItem>
                      </div>
                      {!value && !isEmpty(sciPendingSettingData) && (
                        <span className={classes.sensorPendingStatus}>
                          {pendingSensorValueCheck(index, item)}
                        </span>
                      )}
                    </div>
                  )}
                  <div>
                    {isTemperatureTextFields(item) && (
                      <div className={classes.flexStyle} style={{position: 'relative'}}>
                        <div className={isBulkAction ? classes.bulkSensorFormFields : classes.sensorFormFields}>
                          {isMinLightLevel(key) && (
                            <FormItem halfWidth>
                              <FormTextField placeholder={SCITemperatureFields[key]}
                                value={value}
                                disabled={noUpdate}
                                variant='standard'
                                styleOverrides={textFieldStyleOverrides}
                                onChange={function(e) {
                                  handleSensorsFieldChange(e, item.label, key);
                                }}/>
                            </FormItem>
                          )}
                          {isMaxLightLevel(key) && (
                            <FormItem halfWidth>
                              <FormTextField placeholder={SCITemperatureFields[key]}
                                value={value}
                                variant='standard'
                                disabled={noUpdate}
                                styleOverrides={textFieldStyleOverrides}
                                onChange={function(e) {
                                  handleSensorsFieldChange(e, item.label, key);
                                }}/>
                            </FormItem>
                          )}
                        </div>
                        {(isTemperatureLoggingFields(key)) && (
                          !isEmpty(sciPendingSettingData) && (
                            <span className={classes.sensorMaxTempStyle}>
                              {pendingSensorValueCheck(index, item)}
                            </span>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>);
        } else {
          return (
            <div>
              <div className={classes.flexStyle} style={loggingFieldStyle}>
                <div className={bulkSCI ? classes.sensorTemperatureFiels : classes.sensorFiels}>
                  <FormItem fullWidth>
                    <div className={classes.actionsTile}>
                      <BodyTypography className={classes.overridesReportTitle}>
                        {deviceModel === SCIDeviceType.STAG09XT
                          ? SCITemperatureFields[deviceModel]
                          : item.label
                        }
                      </BodyTypography>
                      {!bulkSCI
                        ? <Switch disabled={noUpdate}
                          name={item.label}
                          onChange={function(e) {
                            handleSensorsFieldChange(!item.temperatureLogging, item.label, 'temperatureLogging');
                          }}
                          checked={deviceModel === SCIDeviceType.STAG09XT
                            ? true
                            : getIsActive(item.temperatureLogging)}/>
                        : getRadioField(item, item.temperatureLogging, 'temperatureLogging')}
                    </div>
                  </FormItem>
                </div>
                {!isEmpty(sciPendingSettingData) && (
                  <div className={classes.sensorPendingStatus} style={item.temperatureLogging ? {display: 'none'} : {}}>
                    {getPendingStatus(index, item)}
                  </div>
                )}
              </div>
              <div>
                {isSensorLogging(item) && (
                  <div className={classes.flexStyle} style={loggingFieldStyle}>
                    <div className={isBulkAction ? classes.bulkSensorFormFields : classes.sensorFormFields}>
                      <FormItem halfWidth>
                        <FormTextField placeholder={translate('admin-devices:view.sciSettings.sensorLoggingPeriodSec')}
                          value={item.sensorLoggingPeriod}
                          variant='standard'
                          disabled={noUpdate}
                          styleOverrides={textFieldStyleOverrides}
                          onChange={function(e) {
                            handleSensorsFieldChange(e, item.label, 'sensorLoggingPeriod');
                          }}/>
                      </FormItem>
                    </div>
                    {!isEmpty(sciPendingSettingData) && (
                      <div className={item.syncSensorLog ? classes.sensorPendingStatus : classes.syncSensorLogStyle}>
                        {pendingSensorValueCheck(index, item)}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {isSyncSensorLog(item) && (
                <div className={isBulkAction ? classes.bulkSensorFormFields : classes.sensorFormFields}>
                  <FormItem fullWidth>
                    <div className={classes.actionsTile}>
                      <BodyTypography className={classes.overridesReportTitle}>
                        {translate('admin-devices:view.sciSettings.syncLoggingandReporting')}
                      </BodyTypography>
                      {!bulkSCI
                        ? <Switch disabled={noUpdate}
                          name={item.label}
                          onChange={function(e) {
                            handleSensorsFieldChange(!item.syncSensorLog, item.label, 'syncSensorLog');
                          }}
                          checked={getIsActive(item.syncSensorLog)}/>
                        : getRadioField(item, item.syncSensorLog, 'syncSensorLog')}
                    </div>
                  </FormItem>
                </div>)
              }
              <div>
                {isSyncSensorLogTemp(item) && (
                  <div className={isBulkAction ? classes.bulkSensorFormFields : classes.sensorFormFields}>
                    <FormItem halfWidth>
                      <FormTextField placeholder={translate('admin-devices:view.sciSettings.maxActiveTemperature')}
                        value={item.syncSensorLogTempLimit}
                        variant='standard'
                        disabled={noUpdate}
                        type='number'
                        styleOverrides={textFieldStyleOverrides}
                        onChange={function(e) {
                          handleSensorsFieldChange(e, item.label, 'syncSensorLogTempLimit');
                        }}/>
                    </FormItem>
                  </div>
                )}
              </div>
              {isSensorEvenMode(item) && (
                <div className={!isBulkAction && classes?.sensorFiels}>
                  <FormItem fullWidth>
                    <div className={classes.actionsTile}>
                      <BodyTypography className={classes.overridesReportTitle}>
                        {translate('admin-devices:view.sciSettings.sensorLoggingPeriod')}
                      </BodyTypography>
                      <div className={classes?.sensorSubFormWidth}>
                        <ReportDropdownChooser options={sensorLogOptions}
                          readOnly={noUpdate}
                          style={dropdownStyle}
                          onChange={function(e) {
                            handleSensorsFieldChange(e, item.label, 'sensorEventMode');
                          }}
                          defaultValue={sensorLoggingPeriodList[item.sensorEventMode]}/>
                      </div>
                    </div>
                  </FormItem>
                </div>
              )}
            </div>
          );
        }
      }) : ''}
    </div>
  );
};

export default SCISensorsTab;
